import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { 
  CreditCard, 
  Check, 
  Calendar,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Star,
  Shield,
  Timer,
  X
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService, TrialStatusResponse } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import { SUPPORT_EMAIL } from '../constants/contact'
import emailService from '../services/emailService'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAudit } from '../hooks/useAudit'
import PostbackService from '../services/postbackService'

const SubscriptionV3 = () => {
  const { user, subscription, checkSubscription, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const { testAuditLogging } = useAudit()
  const [localLoading, setLocalLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    details?: Record<string, string>
  } | null>(null)
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentFormDisplayed, setPaymentFormDisplayed] = useState(false)
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null)
  const [currentTrialSubscriptionId, setCurrentTrialSubscriptionId] = useState<string | null>(null)
  const [currentFormToken, setCurrentFormToken] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const { t, formatPrice, config } = useLocalization()

  useEffect(() => {
    // Attendre que l'utilisateur soit chargé avant de charger l'abonnement
    if (user?.email && !authLoading) {
      loadSubscriptionInfo()
    }
    loadSystemPayScript()
  }, [user?.email, authLoading])

  // Redirection automatique seulement si l'utilisateur arrive depuis une autre page avec un abonnement actif
  // Mais permettre l'accès direct à la page d'abonnement pour voir le statut
  useEffect(() => {
    if (!authLoading && subscription && subscription.status === 'active') {
      // Pour les abonnements actifs, permettre la visualisation du statut sur cette page
      // Ne pas rediriger automatiquement
      console.log('👁️ Utilisateur avec abonnement actif - affichage du statut sur la page d\'abonnement')
    }
  }, [authLoading, subscription])

  const loadSystemPayScript = () => {
    // Charger le script SystemPay avec la bonne langue
    if (!document.querySelector('script[src*="kr-payment-form"]')) {
      const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
      
      const script = document.createElement('script')
      script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
      script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
      script.setAttribute('kr-language', systemPayLanguage)
      document.head.appendChild(script)

      console.log('🌍 SystemPay chargé avec la langue:', systemPayLanguage)

      // Charger le thème Neon
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
      document.head.appendChild(link)

      const neonScript = document.createElement('script')
      neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
      document.head.appendChild(neonScript)
    }
  }

  const loadSubscriptionInfo = async () => {
    try {
      setLocalLoading(true)
      await checkSubscription()
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error)
    } finally {
      setLocalLoading(false)
    }
  }

  const handleSubscribe = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Début de la création d\'abonnement')

      const result = await subscriptionService.createSubscriptionWithPayment(user.email)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        
        // Afficher le formulaire de paiement SystemPay
        await displayPaymentForm(result.data.formToken)
        setShowPaymentForm(true)
        
        // Démarrer la surveillance du paiement
        startPaymentPolling(result.data.orderId)
        
      } else {
        throw new Error(result.message || 'Erreur lors de la création du paiement')
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const displayPaymentForm = async (formToken: string) => {
    try {
      // Attendre que le script SystemPay soit chargé
      await new Promise<void>((resolve) => {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
      })

      if (window.KR) {
        const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
        console.log('🌍 Configuration du formulaire SystemPay avec la langue:', systemPayLanguage)
        
        await window.KR.setFormConfig({
          formToken: formToken,
          'kr-language': systemPayLanguage
        })

        const container = document.getElementById('payment-container')
        if (container) {
          container.innerHTML = '<div class="kr-smart-form" kr-no-card-logo-header></div>'
          await window.KR.renderElements('#payment-container')

          // Marquer que le formulaire est affiché
          setPaymentFormDisplayed(true)
        }
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'affichage du formulaire:', error)
      throw error
    }
  }

  const startPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Envoyer le postback de paiement réussi avec l'event_id
            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              console.log('💰 Paiement validé, envoi du postback avec event_id:', eventId)
              PostbackService.sendPaymentPostback(eventId, 0)
            } else {
              console.log('⚠️ Pas d\'event_id trouvé, postback ignoré')
            }

            // Tracker le succès de l'abonnement d'essai V3
            AnalyticsService.trackSubscription('trial_v3')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v3_success',
              currency: config?.currency || 'USD',
              value: 0, // FREE
            })

            // Envoyer l'email de bienvenue d'essai de manière asynchrone
            if (user?.email) {
              console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', user.email)
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => {
                  console.warn('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue (non bloquant):', error)
                })
            }

            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message')
            })

            // Marquer que le paiement a réussi pour éviter l'affichage de la section d'essai
            setPaymentSuccess(true)

            // Reload subscription info
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentFailedTitle'),
              message: t('subscriptionV3.paymentFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentCancelledTitle'),
              message: t('subscriptionV3.paymentCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification du statut:', error)
      }
    }, 2000)

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleStartTrialV3 = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Starting trial subscription V3 for:', user.email)
      
      // Tracker le début de l'essai V3
      AnalyticsService.trackEvent('begin_checkout', {
        event_category: 'subscription',
        event_label: 'trial_v3_start',
        currency: config?.currency || 'USD',
        value: 0, // FREE - No charges in V3
      })

      // Récupérer le nom complet de l'utilisateur
      const fullName = user.user_metadata?.full_name || user.email.split('@')[0]
      console.log('👤 Nom utilisateur:', fullName)

      const result = await subscriptionService.createTrialSubscriptionV3(user.email, user.id, fullName)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)
        
        // Show payment form section
        setShowPaymentForm(true)
        
        // Display payment form directly
        console.log('🔧 Displaying payment form directly with token:', result.data.formToken)
        await displayPaymentForm(result.data.formToken)
        
        // Start payment polling (same as V1)
        startTrialPaymentPolling(result.data.orderId)
        
      } else {
        throw new Error(result.message || 'Error creating trial subscription V3')
      }

    } catch (error) {
      console.error('❌ Error:', error)
      
      // Tracker l'erreur de création d'essai V3
      AnalyticsService.trackError('trial_v3_creation_error', error instanceof Error ? error.message : 'Unknown trial V3 creation error')
      
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const startTrialPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Envoyer le postback de paiement réussi pour l'essai avec l'event_id
            const eventId = user?.user_metadata?.event_id
            if (eventId) {
              console.log('💰 Essai validé, envoi du postback avec event_id:', eventId)
              PostbackService.sendPaymentPostback(eventId, 0)
            } else {
              console.log('⚠️ Pas d\'event_id trouvé, postback ignoré')
            }

            // Tracker le succès de l'abonnement d'essai V3
            AnalyticsService.trackSubscription('trial_v3')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v3_success',
              currency: config?.currency || 'USD',
              value: 0, // FREE
            })

            // Envoyer l'email de bienvenue d'essai de manière asynchrone
            if (user?.email) {
              console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', user.email)
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => {
                  console.warn('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue (non bloquant):', error)
                })
            }
            
            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message')
            })

            // Marquer que le paiement a réussi pour éviter l'affichage de la section d'essai
            setPaymentSuccess(true)

            // Reload subscription info
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)
            
          } else if (status === 'failed') {
            clearInterval(pollInterval)
            
            // Tracker l'échec du paiement
            AnalyticsService.trackError('payment_failed', 'Trial V3 payment method registration failed')
            
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationFailedTitle'),
              message: t('subscriptionV3.paymentRegistrationFailedMessage')
            })
            
          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            
            // Tracker l'annulation du paiement
            AnalyticsService.trackEvent('payment_cancelled', {
              event_category: 'subscription',
              event_label: 'trial_v3_payment_cancelled',
            })
            
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationCancelledTitle'),
              message: t('subscriptionV3.paymentRegistrationCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error)
      }
    }, 2000)

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleCancelSubscription = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    const confirmMessage = subscription.status === 'trial' 
      ? t('subscriptionV3.confirmCancelTrial')
      : t('subscriptionV3.confirmCancelSubscription')

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setCancelLoading(true)
      console.log('🚫 Début de l\'annulation pour:', user.email)
      
      // Utiliser la nouvelle méthode qui récupère automatiquement l'ID
      const result = await subscriptionService.cancelSubscription(user.email, true)
      
      if (result.success) {
        // Tracker l'annulation d'abonnement V3
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: subscription.status === 'trial' ? 'trial_v3_cancelled' : 'subscription_v3_cancelled',
        })
        
        // Envoyer l'email d'annulation de manière asynchrone
        if (user?.email) {
          console.log('📧 Envoi de l\'email d\'annulation pour:', user.email, 'Type:', subscriptionType)
          emailService.sendCancellationEmailAsync(
            user.email, 
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => {
            console.warn('⚠️ Erreur lors de l\'envoi de l\'email d\'annulation (non bloquant):', error)
          })
        }
        
        const successMessage = subscription.status === 'trial' 
          ? t('subscriptionV3.cancelSuccessTrial')
          : t('subscriptionV3.cancelSuccessSubscription')
        
        alert(successMessage)
        
        // Forcer le refresh du cache pour s'assurer que l'API est interrogée
        await checkSubscription(true)
        
        // Rediriger vers la page d'accueil après 2 secondes
        setTimeout(() => {
          navigate('/')
        }, 2000)
        
      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error)
      
      const errorMessage = error instanceof Error ? error.message : t('errors.generic')
      alert(`${t('errors.generic')}: ${errorMessage}`)
      
      // Si l'erreur indique qu'aucun abonnement n'a été trouvé, recharger les infos
      if (errorMessage.includes('Aucun abonnement trouvé') || errorMessage.includes('No subscription found')) {
        console.log('🔄 Rechargement des informations d\'abonnement après erreur')
        await loadSubscriptionInfo()
      }
      
    } finally {
      setCancelLoading(false)
    }
  }

  const getStatusInfo = () => {
    if (!subscription) return { status: 'none', message: '', color: 'gray' }
    
    if (subscription.status === 'active') {
      return {
        status: 'active',
        message: 'Active',
        color: 'green'
      }
    }
    
    if (subscription.status === 'trial') {
      return {
        status: 'trial',
        message: t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext }),
        color: 'blue'
      }
    }
    
    if (subscription.status === 'cancelled') {
      return {
        status: 'cancelled',
        message: `${t('common.cancelled')} - ${t('subscriptionV3.accessRemains')} ${subscription.expiryDate}`,
        color: 'orange'
      }
    }
    
    return {
      status: subscription.status,
      message: subscription.status,
      color: 'gray'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      
      {/* Main content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Loader pendant la vérification de l'abonnement */}
        {authLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('common.loading')}</p>
            </div>
          </div>
        )}

        {/* Spinner pendant la redirection après paiement réussi */}
        {paymentSuccess && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="p-6">
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mb-6"></div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('subscription.trial.activated')}
                </h3>
                <p className="text-gray-600 text-center">
                  {t('subscriptionV3.redirectingToDashboard')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Affichage de l'abonnement d'essai actif */}
        {!authLoading && subscription && subscription.status === 'trial' && !paymentSuccess && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="p-6">
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Timer className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('subscriptionV3.trialActive')}
                  </h4>
                  <div className="text-center mb-4">
                    <div className="text-lg font-semibold text-blue-600 mb-1">
                      {t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('subscriptionV3.trialEndsOn')} {subscription.trialEndDate ? new Date(subscription.trialEndDate).toLocaleDateString() : subscription.expiryDate}
                    </div>
                  </div>
                  
                  {/* Trial benefits */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                      <div className="text-left">
                        <h5 className="text-sm font-medium text-green-900 mb-1">
                          {t('subscriptionV3.fullAccessTitle')}
                        </h5>
                        <p className="text-sm text-green-700">
                          {t('subscriptionV3.fullAccessDescription')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">{t('landing.features.smartMap.title')}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">{t('landing.features.saveTime.description')}</span>
                  </div>
                  <div className="flex items-center">
                    <Navigation className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">{t('landing.features.navigation.title')}</span>
                  </div>
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-700">{t('landing.features.favorites.title')}</span>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="bg-red-600 text-white py-2 px-6 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 mr-4"
                  >
                    {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelTrial')}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('subscriptionV3.cancelTrialInfo')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pas d'abonnement ou abonnement expiré */}
        {!authLoading && (!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="p-6">
              {!showPaymentForm ? (
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="text-center mb-6">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {t('subscriptionV3.noSubscriptionTitle')}
                        </div>
                      </div>
                    </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('subscriptionV3.simpleFeatures.interactiveMap')}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('subscriptionV3.simpleFeatures.realTimeAvailability')}</span>
                    </div>
                    <div className="flex items-center">
                      <Navigation className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('subscriptionV3.simpleFeatures.gpsNavigation')}</span>
                    </div>

                    <div className="flex items-center">
                      <Shield className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('subscriptionV3.simpleFeatures.noUpfrontCharges')}</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-700 mb-4">
                      {t('subscriptionV3.accountActivationRequired')}
                    </p>
                    <button
                      onClick={handleStartTrialV3}
                      disabled={paymentLoading}
                      className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-8 rounded-lg text-lg font-medium hover:from-green-600 hover:to-green-700 transform hover:scale-105 transition-all duration-500 disabled:opacity-50 shadow-xl shadow-green-300/30 hover:shadow-green-400/50 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                    >
                      {paymentLoading ? t('subscriptionV3.processing') : t('subscriptionV3.continue')}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('subscriptionV3.noSubscriptionSubtitle')}
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-center">
                    <p className="text-gray-700 text-sm font-bold">
                      {t('subscriptionV3.paymentMethodRegistration')}
                    </p>
                  </div>

                  {paymentLoading && (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">{t('subscriptionV3.loadingPaymentForm')}</p>
                    </div>
                  )}

                  <div id="payment-container" className="min-h-[350px]">
                    {/* Le formulaire SystemPay sera injecté ici */}
                  </div>

                  {/* Security logos section */}
                  <div className="mt-6 flex items-center justify-center space-x-4 py-4">
                    {/* Visa Logo */}
                    <div className="flex flex-col items-center">
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/196/196578.png"
                        alt="Visa"
                        className="h-8 w-12 object-contain"
                      />
                      <span className="text-xs text-gray-600 mt-1">Visa</span>
                    </div>

                    {/* Mastercard Logo */}
                    <div className="flex flex-col items-center">
                      <img
                        src="https://cdn-icons-png.flaticon.com/512/196/196561.png"
                        alt="Mastercard"
                        className="h-8 w-12 object-contain"
                      />
                      <span className="text-xs text-gray-600 mt-1">Mastercard</span>
                    </div>

                    {/* Credit Card Logo */}
                    <div className="flex flex-col items-center">
                      <img
                        src="https://cdn-icons-png.flaticon.com/128/16009/16009219.png"
                        alt="Credit Card"
                        className="h-8 w-12 object-contain"
                      />
                      <span className="text-xs text-gray-600 mt-1">CB</span>
                    </div>

                    {/* Secure Payment Badge */}
                    <div className="flex flex-col items-center">
                      <img
                        src="https://cdn-icons-png.flaticon.com/128/15496/15496554.png"
                        alt="Sécurisé"
                        className="h-8 w-8 object-contain"
                      />
                      <span className="text-xs text-gray-600 mt-1">Sécurisé</span>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <p className="text-sm text-green-800 font-medium">{t('subscriptionV3.noChargeInfo')}</p>
                      <p className="text-sm text-green-700 mt-2">
                        {t('subscriptionV3.paymentMethodDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Résultat du paiement */}
        {paymentResult && (
          <div className={`mb-8 p-4 rounded-lg ${
            paymentResult.type === 'success' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              {paymentResult.type === 'success' ? (
                <Check className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
              )}
              <div>
                <h4 className={`text-sm font-medium ${
                  paymentResult.type === 'success' ? 'text-green-900' : 'text-red-900'
                }`}>
                  {paymentResult.title}
                </h4>
                <p className={`text-sm mt-1 ${
                  paymentResult.type === 'success' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {paymentResult.message}
                </p>
                {paymentResult.details && (
                  <div className="mt-2 text-sm">
                    {Object.entries(paymentResult.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="font-medium">{key}:</span>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Premium Benefits Section */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {t('subscriptionV3.whyChooseTitle')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Time & Money Savings */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('subscriptionV3.benefits.saveTime.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.saveTime.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('landing.features.smartMap.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.smartMap.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Navigation className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('landing.features.navigation.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.navigation.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Comfort & Peace of Mind */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Star className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('landing.features.favorites.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.favorites.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('subscriptionV3.benefits.notifications.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.notifications.description')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <Shield className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('subscriptionV3.benefits.support.title')}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('subscriptionV3.benefits.support.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Section */}
            <div className="mt-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">
                {t('subscriptionV3.statistics.title')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center mb-6">
                <div>
                  <div className="text-3xl font-bold text-green-600 mb-1">{t('subscriptionV3.statistics.timeSaved')}</div>
                  <div className="text-sm text-gray-600">{t('subscriptionV3.statistics.timeSavedDesc')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-600 mb-1">{t('subscriptionV3.statistics.satisfaction')}</div>
                  <div className="text-sm text-gray-600">{t('subscriptionV3.statistics.satisfactionDesc')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600 mb-1">{t('subscriptionV3.statistics.savings')}</div>
                  <div className="text-sm text-gray-600">{t('subscriptionV3.statistics.savingsDesc')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-600 mb-1">{formatPrice()}</div>
                  <div className="text-sm text-gray-600">{t('subscriptionV3.statistics.priceDesc')}</div>
                </div>
              </div>
              
              {/* Call to Action Button */}
              <div className="text-center">
                <button
                  onClick={async () => {
                    // Scroll to the very top of the page (including header)
                    document.documentElement.scrollTop = 0
                    document.body.scrollTop = 0
                    // Small delay to ensure scroll completes before starting trial
                    setTimeout(() => {
                      handleStartTrialV3()
                    }, 100)
                  }}
                  disabled={paymentLoading}
                  className="bg-green-600 text-white py-3 px-8 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {paymentLoading ? t('subscriptionV3.processing') : t('subscriptionV3.continue')}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t('subscriptionV3.noSubscriptionSubtitle')}
                </p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-6 text-center">
              <p className="text-gray-700 text-lg mb-4">
                <strong>{t('subscriptionV3.cta.title')}</strong> {t('subscriptionV3.cta.description')}
              </p>
              <p className="text-sm text-gray-600">
                {t('subscriptionV3.cta.trialInfo')}
              </p>
            </div>
          </div>
        </div>

        {/* Informations de sécurité */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {t('subscriptionV3.security.title')}
            </h3>
          </div>
          <div className="p-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-green-900">
                    {t('subscriptionV3.security.secureTitle')}
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    {t('subscriptionV3.security.secureDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('subscriptionV3.faq.howTrialWorks.question')}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {t('subscriptionV3.faq.howTrialWorks.answer', { price: formatPrice() })}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('subscriptionV3.faq.chargesDuringTrial.question')}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {t('subscriptionV3.faq.chargesDuringTrial.answer')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('subscriptionV3.faq.afterTrial.question')}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {t('subscriptionV3.faq.afterTrial.answer', { price: formatPrice() })}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('subscriptionV3.faq.cancelDuringTrial.question')}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {t('subscriptionV3.faq.cancelDuringTrial.answer')}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  {t('subscriptionV3.faq.needHelp.question')}
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  {t('subscriptionV3.faq.needHelp.answer')}{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-blue-600 hover:text-blue-800">
                    {SUPPORT_EMAIL}
                  </a>
                  {' '}{t('subscriptionV3.faq.needHelp.answerEnd')}
                </p>
              </div>
            </div>


          </div>
        </div>

        {/* Abonnement actif */}
        {!authLoading && subscription && subscription.status === 'active' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {t('subscriptionV3.activeSubscription.title')}
              </h3>
            </div>
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-green-900">
                      {t('subscriptionV3.activeSubscription.statusTitle')}
                    </h4>
                    <p className="text-sm text-green-700 mt-1">
                      {t('subscriptionV3.activeSubscription.statusDescription')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">{t('subscriptionV3.activeSubscription.planDetails')}</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>{t('subscriptionV3.activeSubscription.plan')}:</span>
                      <span className="font-medium">{t('landing.pricing.planName')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscriptionV3.activeSubscription.price')}:</span>
                      <span className="font-medium">{formatPrice(subscription.price)}{t('common.perMonth')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscriptionV3.activeSubscription.status')}:</span>
                      <span className="font-medium text-green-600">{t('subscriptionV3.activeSubscription.active')}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">{t('subscriptionV3.activeSubscription.nextBilling')}</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>{t('subscriptionV3.activeSubscription.nextPayment')}:</span>
                      <span className="font-medium">
                        {subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscriptionV3.activeSubscription.daysUntilBilling')}:</span>
                      <span className="font-medium">{subscription.daysUntilNext || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                  className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelSubscription')}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  {t('subscriptionV3.activeSubscription.cancelInfo')}
                </p>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionV3
